/**
 * Dashboard — Panel principal de la Sección Personal.
 * 
 * Vista matutina interactiva con:
 * - Tareas del día clickeables con detalle, edición y eliminación en modal
 * - Creación rápida de nuevas tareas con asignación a cliente
 * - Checkbox para completar tareas al instante
 * - Integración con temporizador y navegación al cliente
 * - Resumen financiero rápido
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Play,
  AlertTriangle,
  ArrowUpCircle,
  TrendingUp,
  TrendingDown,
  Loader2,
  Plus,
  Trash2,
  X,
  Edit2,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { cn, formatCurrency, formatShortDate } from '@/lib/utils';
import { useTimerStore } from '@/stores/timerStore';
import type { Task, FinanceSummary, Client } from '@/types';
import { PRIORITY_LABELS, PRIORITY_COLORS } from '@/types';
import { TimerWidget } from '@/components/personal/TimerWidget';

const PRIORITY_ICONS: Record<number, React.ReactNode> = {
  0: <Circle size={16} className="text-muted-foreground" />,
  1: <ArrowUpCircle size={16} className="text-blue-500" />,
  2: <AlertTriangle size={16} className="text-amber-500" />,
  3: <AlertTriangle size={16} className="text-red-500 fill-red-500/20" />,
};

export function Dashboard() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterView, setFilterView] = useState<'pending' | 'all'>('pending');
  const timer = useTimerStore();

  // Modal Crear / Editar Tarea
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    client_id: '',
    description: '',
    priority: 1,
    due_date: new Date().toISOString().split('T')[0],
    status: 'pending' as Task['status'],
  });
  const [savingTask, setSavingTask] = useState(false);

  const fetchData = async () => {
    try {
      const [tasksRes, summaryRes, clientsRes] = await Promise.all([
        filterView === 'pending' ? api.getTodayTasks() : api.getTasks({ limit: '40' }),
        api.getFinanceSummary(),
        api.getClients('active'),
      ]);
      setTasks(tasksRes.tasks as Task[]);
      setSummary(summaryRes as FinanceSummary);
      setClients(clientsRes.clients as Client[]);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterView]);

  // ─── Handlers de Tareas ───
  const handleToggleTask = async (task: Task, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await api.updateTaskStatus(task.id, newStatus);
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t))
      );
    } catch (error) {
      console.error('Error actualizando tarea:', error);
    }
  };

  const handleStartTimer = (task: Task, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (task.client_id && task.client_name) {
      timer.start(task.client_id, task.client_name, task.id);
    }
  };

  const openNewTaskModal = () => {
    setEditingTask(null);
    setTaskForm({
      title: '',
      client_id: clients[0]?.id || '',
      description: '',
      priority: 1,
      due_date: new Date().toISOString().split('T')[0],
      status: 'pending',
    });
    setModalOpen(true);
  };

  const openEditTaskModal = (task: Task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      client_id: task.client_id || '',
      description: task.description || '',
      priority: task.priority,
      due_date: task.due_date || new Date().toISOString().split('T')[0],
      status: task.status,
    });
    setModalOpen(true);
  };

  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskForm.title.trim()) return;

    setSavingTask(true);
    try {
      if (editingTask) {
        await api.updateTask(editingTask.id, {
          ...taskForm,
          client_id: taskForm.client_id || null,
        });
      } else {
        await api.createTask({
          ...taskForm,
          client_id: taskForm.client_id || null,
        });
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error guardando tarea:', error);
      alert('Error guardando la tarea');
    } finally {
      setSavingTask(false);
    }
  };

  const handleDeleteTask = async (id: string) => {
    if (!confirm('¿Eliminar esta tarea?')) return;
    try {
      await api.deleteTask(id);
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error eliminando tarea:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingCount = tasks.filter((t) => t.status !== 'completed').length;

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Mi Día</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Planifica tus prioridades, controla tus horas y revisa tu estado financiero.
          </p>
        </div>

        <Button size="sm" onClick={openNewTaskModal} className="gap-1.5 shadow-sm text-xs">
          <Plus size={14} />
          Nueva Tarea
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─── Tareas del Día ─── */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border border-slate-200/90 shadow-xs">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 size={18} className="text-indigo-600" />
                  Tareas
                  <Badge variant="secondary" className="text-xs">
                    {pendingCount} pendientes
                  </Badge>
                </CardTitle>

                {/* Filtro rápido: Pendientes / Todas */}
                <div className="flex items-center bg-slate-100 p-0.5 rounded-lg text-xs self-start sm:self-auto">
                  <button
                    onClick={() => setFilterView('pending')}
                    className={cn(
                      'px-2.5 py-1 rounded-md transition-colors font-medium',
                      filterView === 'pending'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    )}
                  >
                    Hoy / Pendientes
                  </button>
                  <button
                    onClick={() => setFilterView('all')}
                    className={cn(
                      'px-2.5 py-1 rounded-md transition-colors font-medium',
                      filterView === 'all'
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    )}
                  >
                    Ver Todas
                  </button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-4">
              {tasks.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 size={24} />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-800">
                    ¡No hay tareas pendientes!
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                    Aprovecha para adelantar trabajo o crea una nueva tarea para hoy.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openNewTaskModal}
                    className="mt-4 gap-1.5 text-xs"
                  >
                    <Plus size={13} />
                    Agregar Tarea
                  </Button>
                </div>
              ) : (
                <ul className="space-y-2">
                  {tasks.map((task) => {
                    const isDone = task.status === 'completed';
                    return (
                      <li
                        key={task.id}
                        onClick={() => openEditTaskModal(task)}
                        className={cn(
                          'group flex items-center gap-3 rounded-xl border p-3 transition-all cursor-pointer',
                          'hover:border-slate-300 hover:shadow-xs hover:bg-slate-50/50',
                          isDone ? 'bg-slate-50/40 opacity-60' : 'bg-white border-slate-200/90'
                        )}
                      >
                        {/* Checkbox para completar con un clic */}
                        <button
                          onClick={(e) => handleToggleTask(task, e)}
                          className="shrink-0 p-1 hover:scale-110 transition-transform"
                          title={isDone ? 'Marcar como pendiente' : 'Marcar como completada'}
                        >
                          {isDone ? (
                            <CheckCircle2 size={18} className="text-emerald-500 fill-emerald-50" />
                          ) : (
                            PRIORITY_ICONS[task.priority]
                          )}
                        </button>

                        {/* Task info */}
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              'text-sm font-medium truncate text-slate-900 group-hover:text-indigo-600 transition-colors',
                              isDone && 'line-through text-slate-400'
                            )}
                          >
                            {task.title}
                          </p>

                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {task.client_name && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.client_id) navigate(`/clientes/${task.client_id}`);
                                }}
                                className="text-[11px] px-2 py-0.5 rounded-full font-medium hover:underline flex items-center gap-1 transition-opacity"
                                style={{
                                  backgroundColor: `${task.avatar_color || '#6366f1'}15`,
                                  color: task.avatar_color || '#6366f1',
                                }}
                                title="Ir a la ficha del cliente"
                              >
                                {task.client_name}
                                <ExternalLink size={9} />
                              </button>
                            )}

                            {task.due_date && (
                              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                <Clock size={11} />
                                {formatShortDate(task.due_date)}
                              </span>
                            )}

                            <span
                              className={cn(
                                'text-[11px] font-medium',
                                PRIORITY_COLORS[task.priority]
                              )}
                            >
                              {PRIORITY_LABELS[task.priority]}
                            </span>
                          </div>
                        </div>

                        {/* Quick action buttons */}
                        <div
                          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {task.client_id && !isDone && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
                              onClick={(e) => handleStartTimer(task, e)}
                              title="Iniciar temporizador para esta tarea"
                            >
                              <Play size={13} />
                            </Button>
                          )}

                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            onClick={() => openEditTaskModal(task)}
                            title="Editar tarea"
                          >
                            <Edit2 size={13} />
                          </Button>

                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            onClick={() => handleDeleteTask(task.id)}
                            title="Eliminar tarea"
                          >
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── Panel Derecho ─── */}
        <div className="space-y-4">
          {/* Timer Widget */}
          <TimerWidget />

          {/* Resumen Financiero */}
          {summary && (
            <Card className="border border-slate-200/90 shadow-xs">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-base flex items-center gap-2">
                  💰 Resumen del Mes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <TrendingUp size={14} className="text-emerald-500" />
                    Ingresos
                  </span>
                  <span className="font-semibold text-emerald-600">
                    {formatCurrency(summary.income.total)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <TrendingDown size={14} className="text-red-500" />
                    Gastos
                  </span>
                  <span className="font-semibold text-red-500">
                    {formatCurrency(summary.expense.total)}
                  </span>
                </div>
                <div className="border-t pt-3 flex items-center justify-between">
                  <span className="text-sm font-medium">Balance</span>
                  <span
                    className={cn(
                      'font-bold text-lg',
                      summary.balance >= 0 ? 'text-emerald-600' : 'text-red-500'
                    )}
                  >
                    {formatCurrency(summary.balance)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ─── Modal: Crear / Editar Tarea ─── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingTask ? 'Detalle y Edición de Tarea' : 'Nueva Tarea'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveTask} className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Título de la Tarea *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Programar historias del fin de semana"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Cliente Asociado
                  </label>
                  <select
                    value={taskForm.client_id}
                    onChange={(e) => setTaskForm({ ...taskForm, client_id: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Personal / Sin Cliente</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Prioridad
                  </label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, priority: parseInt(e.target.value, 10) })
                    }
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                  >
                    <option value={0}>Baja (0)</option>
                    <option value={1}>Media (1)</option>
                    <option value={2}>Alta (2)</option>
                    <option value={3}>🚨 Urgente (3)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Fecha de Vencimiento
                  </label>
                  <input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Estado
                  </label>
                  <select
                    value={taskForm.status}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, status: e.target.value as Task['status'] })
                    }
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_progress">En Progreso</option>
                    <option value="completed">Completada ✓</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Descripción o Notas
                </label>
                <textarea
                  rows={3}
                  placeholder="Detalles sobre lo que se debe hacer..."
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                {editingTask ? (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTask(editingTask.id)}
                      className="text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1"
                    >
                      <Trash2 size={13} />
                      Eliminar
                    </Button>

                    {editingTask.client_id && editingTask.status !== 'completed' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          handleStartTimer(editingTask);
                          setModalOpen(false);
                        }}
                        className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50 gap-1"
                      >
                        <Play size={12} />
                        Iniciar Tiempo
                      </Button>
                    )}
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setModalOpen(false)}
                    className="text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={savingTask}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {savingTask ? <Loader2 size={13} className="animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
